// canvasStore.ts
import { atom, createStore } from "jotai";

// Recording quality type
export type RecordingQuality = "720p" | "1080p" | "2k";

// Wrapper width atom
export const wrapperWidthAtom = atom<number>(0);

// Wrapper height atom
export const wrapperHeightAtom = atom<number>(0);

// Recording quality atom
export const recordingQualityAtom = atom<RecordingQuality>("1080p");

// Canvas ratio type
export type CanvasRatio = "16:9" | "1:1" | "9:16";

// Derived atom for wrapper dimensions
export const wrapperDimensionsAtom = atom(
  (get) => ({
    width: get(wrapperWidthAtom),
    height: get(wrapperHeightAtom),
  }),
  (get, set, dimensions: { width: number; height: number }) => {
    set(wrapperWidthAtom, dimensions.width);
    set(wrapperHeightAtom, dimensions.height);
  }
);

// Action atom to set wrapper width
export const setWrapperWidthAtom = atom(null, (get, set, width: number) => {
  set(wrapperWidthAtom, width);
});

// Action atom to set wrapper height
export const setWrapperHeightAtom = atom(null, (get, set, height: number) => {
  set(wrapperHeightAtom, height);
});

// Action atom to set both wrapper dimensions
export const setWrapperDimensionsAtom = atom(
  null,
  (get, set, dimensions: { width: number; height: number }) => {
    set(wrapperWidthAtom, dimensions.width);
    set(wrapperHeightAtom, dimensions.height);
  }
);

// Action atom to reset wrapper dimensions
export const resetWrapperDimensionsAtom = atom(null, (get, set) => {
  set(wrapperWidthAtom, 0);
  set(wrapperHeightAtom, 0);
});

// Action atom to set recording quality
export const setRecordingQualityAtom = atom(
  null,
  (get, set, quality: RecordingQuality) => {
    set(recordingQualityAtom, quality);
  }
);

// Ratio transition state - used to hide existing objects during ratio changes
// When true, all DraggableResizableStream components should hide their objects
export const isRatioTransitioningAtom = atom<boolean>(false);

// Canvas transition snapshot - stores snapshot data with dimensions and offsets for smooth crossfade during quality/ratio changes
// When set, CanvasStage renders this as an overlay while the canvas resizes underneath
export interface CanvasTransitionSnapshot {
  dataUrl: string;
  width: number;
  height: number;
  offsetLeft: number;
  offsetTop: number;
}
export const canvasTransitionSnapshotAtom = atom<CanvasTransitionSnapshot | null>(null);
